import { FC, useEffect, useState } from 'react'
import { useSearchParams } from '@/hooks/useSearchParams'
import usePagination from '@/hooks/usePagination'
import Pagination from '@UI/New/Pagination'
import { reportAPI } from '@/api'
import CustomTable from '@common/CustomTable/Table'
import { IPrivateAllLessonItem } from '../../utils/types'
import usePrivateAllLessonsColumns from '../TableColumns/usePrivateAllLessonsColumns'
import cn from '../PrivateLessonReportTable/index.module.css'
import ChangeLessonReportStatusModal from '../../../GroupReport/components/MakeLessonReportModal/ChangeLessonReportStatusModal'
import EditEntityModal from '@modals/EditEntityModal/EditEntityModal'
import useRoles from '@/hooks/useRoles'

interface IPrivateAllLessonsTable {
  studentId?: number | string | null
  directionId?: string | null
  startDate?: string | null
  endDate?: string | null
  status?: string | null
  onReportChange?: () => void
}

const PrivateAllLessonsTable: FC<IPrivateAllLessonsTable> = ({
  studentId,
  directionId: selectedDirectionId,
  startDate,
  endDate,
  status,
  onReportChange,
}) => {
  const { isAdminOrJuniorAdmin } = useRoles()
  const [startDateFromSearch] = useSearchParams('all-start-date')
  const [endDateFromSearch] = useSearchParams('all-end-date')
  const [directionId] = useSearchParams('direction-id')
  const [seminarianId] = useSearchParams('all-seminarian-id')
  const [studentIdFromSearch] = useSearchParams('all-student-id')
  const [statusFromSearch] = useSearchParams('all-status')
  const [search] = useSearchParams('all-search')
  const [franchiseIds] = useSearchParams('franchise-ids')
  const [editReport, setEditReport] = useState<IPrivateAllLessonItem | null>(
    null,
  )
  const [userModal, setUserModal] = useState<{
    user: IUser
    role: 'student' | 'employee'
  } | null>(null)
  const [sorting, setSorting] = useState(
    [
      'date-time',
      'seminarian',
      'student',
      'course',
      'duration',
      'status',
      'price',
      'salary',
      'comment',
    ].map((key) => ({
      key,
      order: 'none' as 'asc' | 'desc' | 'none',
    })),
  )
  const currentSort = sorting.find((item) => item.order !== 'none')
  const effectiveStartDate = startDate ?? startDateFromSearch
  const effectiveEndDate = endDate ?? endDateFromSearch
  const effectiveStudentId = studentId ?? studentIdFromSearch
  const effectiveStatus = status ?? statusFromSearch
  const effectiveDirectionId = selectedDirectionId === undefined ? directionId : selectedDirectionId

  const { state, loading, getState, ...params } = usePagination({
    api: reportAPI.private.allLessons,
    params: {
      start_date: effectiveStartDate,
      end_date: effectiveEndDate,
      ...(effectiveDirectionId ? { direction_id: effectiveDirectionId } : {}),
      ...(seminarianId ? { seminarian_id: seminarianId } : {}),
      ...(effectiveStudentId ? { student_id: effectiveStudentId } : {}),
      ...(effectiveStatus && effectiveStatus !== 'all'
        ? { status: effectiveStatus }
        : {}),
      ...(search ? { search } : {}),
      ...(franchiseIds ? { franchise_ids: franchiseIds } : {}),
      ...(currentSort
        ? { sort_key: currentSort.key, sort_order: currentSort.order }
        : {}),
    },
    firstRequest: false,
    initialPerPage: 100,
  })

  useEffect(() => {
    getState()
    params.setCurrentPage(1)
  }, [
    effectiveStartDate,
    effectiveEndDate,
    effectiveDirectionId,
    seminarianId,
    effectiveStudentId,
    effectiveStatus,
    search,
    franchiseIds,
    currentSort?.key,
    currentSort?.order,
  ])

  const columns = usePrivateAllLessonsColumns({
    onEdit: (report) => setEditReport(report),
    canOpenSeminarianCards: isAdminOrJuniorAdmin,
    onStudentClick: (user) => {
      if (!user?.id) return
      setUserModal({ user, role: 'student' })
    },
    onSeminarianClick: (user) => {
      if (!isAdminOrJuniorAdmin) return
      if (!user?.id) return
      setUserModal({ user, role: 'employee' })
    },
  })

  return (
    <div className={cn.content}>
      <CustomTable<IPrivateAllLessonItem>
        data={state as IPrivateAllLessonItem[]}
        columns={columns}
        loading={loading}
        sorting={sorting}
        onSortingClick={(key) => {
          setSorting((prev) =>
            prev.map((item) => ({
              ...item,
              order:
                item.key === key
                  ? item.order === 'none'
                    ? 'asc'
                    : item.order === 'asc'
                    ? 'desc'
                    : 'none'
                  : 'none',
            })),
          )
        }}
      />
      <Pagination {...params} />
      <ChangeLessonReportStatusModal
        open={!!editReport}
        report={editReport ? { ...editReport, conference_type: 'private' } as any : null}
        onSuccess={() => {
          getState()
          onReportChange?.()
          setEditReport(null)
        }}
        onDismiss={() => setEditReport(null)}
      />
      {userModal?.role === 'employee' && (
        <EditEntityModal
          type="employee"
          open={!!userModal}
          employeeId={userModal?.user?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
      {userModal?.role === 'student' && (
        <EditEntityModal
          type="student"
          open={!!userModal}
          studentId={userModal?.user?.id ?? null}
          onDismiss={() => setUserModal(null)}
          onUpdate={() => setUserModal(null)}
        />
      )}
    </div>
  )
}

export default PrivateAllLessonsTable
